import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LeftAccentCard from '../components/LeftAccentCard';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import NoteIcon from '@mui/icons-material/Note';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { green } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import { useLocation, useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PageHeader from '../components/PageHeader';

// API関連のインポート
import { applicationApi, jobPostingApi, jobShiftApi, jobPositionApi, laborConditionsApi, getApiErrorMessage } from '../services/api';
import { JobApplication, JobPostingTemplate, LaborConditionsAgreementInfo } from '../types';
import { useAuth } from '../auth/AuthContext';
import JapaneseDatePicker from '../components/common/JapaneseDatePicker';

// JobApplicationタイプを拡張してメモフィールドを追加
interface ExtendedJobApplication extends JobApplication {
  notes?: string;
  shift_id?: string;
  memo?: string;
  user_rank?: 'A' | 'B' | null;
  // 課題114: 採用確定で生成された勤怠の状態（planned のときのみ「勤怠をキャンセル」可能）
  work_attendance_status?: string | null;
}

// 採用ステータスの表示定義（クルー管理は accepted の応募を持つクルーのみ表示するため、
// ここで採用に変更しない限り新規応募者はクルー一覧に載らない）
const APPLICATION_STATUS_CONFIG: {
  [key: string]: { label: string; color: 'info' | 'warning' | 'success' | 'error' | 'default' };
} = {
  submitted: { label: '応募中', color: 'info' },
  under_review: { label: '審査中', color: 'warning' },
  accepted: { label: '採用', color: 'success' },
  rejected: { label: '不採用', color: 'error' },
  cancelled: { label: 'キャンセル', color: 'default' },
  no_show: { label: '無断欠勤', color: 'default' },
};

const Applicants: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  
  const [applications, setApplications] = useState<ExtendedJobApplication[]>([]);
  const [jobTemplates, setJobTemplates] = useState<JobPostingTemplate[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>('all');
  const [selectedShift, setSelectedShift] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplicationShift, setSelectedApplicationShift] = useState<any>(null);
  const [jobPositions, setJobPositions] = useState<any[]>([]);
  const [shiftDataCache, setShiftDataCache] = useState<{[key: string]: any}>({});
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { storeId } = useAuth(); // AuthContextから店舗IDを取得
  
  // 勤務日フィルター用のstate
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);  
  // 応募詳細ダイアログ用の状態
  const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
  const [selectedApplication, setSelectedApplication] = useState<ExtendedJobApplication | null>(null);
  // 同意済み労働条件通知書（PDFがある場合のみセット）
  const [laborAgreement, setLaborAgreement] = useState<LaborConditionsAgreementInfo | null>(null);
  
  // 編集ダイアログ用の状態
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editingApplication, setEditingApplication] = useState<ExtendedJobApplication | null>(null);
  const [editNotes, setEditNotes] = useState<string>('');
  const [editRank, setEditRank] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [expandedMobileId, setExpandedMobileId] = useState<string | null>(null);
  // 課題114: 採用確定済み応募の勤怠キャンセル用
  const [cancelAttendanceReason, setCancelAttendanceReason] = useState<string>('');
  const [isCancellingAttendance, setIsCancellingAttendance] = useState<boolean>(false);

  // CSVダウンロード機能
  const convertToCSV = (data: ExtendedJobApplication[]): string => {
    const headers = [
      'クルー名',
      '職種',
      '勤務日',
      '勤務開始時刻',
      '勤務終了時刻',
      'メールアドレス',
      '電話番号',
      '応募日時',
      'メモ',
      '求人',
      '求人ID',
      '評価',
      '給与',
      '給与合計',
      '手当',
      '交通費',
    ];

    const csvData = data.map(application => {
      const shiftData = application.shift_id ? shiftDataCache[application.shift_id] : null;
      const workDate = shiftData ? formatShiftDate(shiftData.shift_date) : '';
      const startTime = shiftData ? formatShiftTime(shiftData.start_time) : '';
      const endTime = shiftData ? formatShiftTime(shiftData.end_time) : '';
      
      // 給与情報を取得
      const salaryType = shiftData?.salary_type || '';
      const salaryAmount = shiftData?.salary_amount || '';
      const salaryAllowance = shiftData?.salary_allowance || '';
      const salaryTransport = shiftData?.salary_transport || '';
      
      // 時給合計の計算
      let hourlyTotal = '';
      if (shiftData && salaryType === 'hourly' && salaryAmount && startTime && endTime && startTime !== '不明' && endTime !== '不明') {
        const workMinutes = calculateWorkMinutes(startTime, endTime, shiftData.break_duration_minutes || 0);
        if (workMinutes > 0) {
          const workHours = workMinutes / 60;
          const total = Math.round(workHours * Number(salaryAmount));
          hourlyTotal = `${total}`;
        }
      } else if (salaryType === 'daily' && salaryAmount) {
        hourlyTotal = salaryAmount;
      }

      return [
        application.applicant_name || '',
        getJobPositionName(application.job_id),
        workDate,
        startTime,
        endTime,
        application.applicant_email || '',
        application.applicant_phone || '',
        formatDateTime(application.applied_date),
        getMemoContent(application),
        getJobTitle(application.job_id),
        application.job_id || '',
        getRankDisplay(application.user_rank),
        salaryAmount ? `${salaryAmount}` : '',
        hourlyTotal,
        salaryAllowance,
        salaryTransport,
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  };

  // 労働時間計算のヘルパー関数
  const calculateWorkMinutes = (startTime: string, endTime: string, breakMinutes: number = 0): number => {
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      const startTotalMinutes = startHour * 60 + startMin;
      let endTotalMinutes = endHour * 60 + endMin;
      
      // 終了時刻が開始時刻より小さい場合（翌日にまたがる場合）
      if (endTotalMinutes <= startTotalMinutes) {
        endTotalMinutes += 24 * 60; // 24時間分を追加
      }
      
      const workMinutes = endTotalMinutes - startTotalMinutes - breakMinutes;
      return Math.max(0, workMinutes); // 負の値は0にする
    } catch (error) {
      return 0;
    }
  };

  const downloadCSV = async () => {
    if (filteredApplications.length === 0) {
      setError('ダウンロードするデータがありません');
      return;
    }

    setIsDownloading(true);
    try {
      // すべてのシフトデータを確実に取得
      const shiftPromises = filteredApplications
        .filter(app => app.shift_id && !shiftDataCache[app.shift_id])
        .map(app => fetchShiftData(app.shift_id!));
      
      await Promise.all(shiftPromises);

      const csvContent = convertToCSV(filteredApplications);
      const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const now = new Date();
      const timestamp = now.toLocaleString('sv-SE', { timeZone: 'Asia/Tokyo' }).replace(/:/g, '-').replace(' ', 'T');
      const filename = `クルー一覧_${timestamp}.csv`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setError(null);
    } catch (err) {
      setError('CSVファイルのダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  };

  // シフトデータを取得する関数をuseCallbackでメモ化
  const fetchShiftData = useCallback(async (shiftId: string) => {
    if (!shiftId || !storeId) return null;

    // キャッシュから取得（関数型更新で最新値を参照）
    let cached: any = null;
    setShiftDataCache(prev => {
      cached = prev[shiftId] || null;
      return prev;
    });
    if (cached) return cached;

    try {
      const shiftData = await jobShiftApi.getById(shiftId, storeId);
      // キャッシュに保存
      setShiftDataCache(prev => ({
        ...prev,
        [shiftId]: shiftData
      }));
      return shiftData;
    } catch (error) {
      return null;
    }
  }, [storeId]);

  useEffect(() => {
    // URLクエリパラメータから応募IDを取得する関数
    const getApplicationIdFromUrl = (): string | null => {
      const params = new URLSearchParams(location.search);
      return params.get('id');
    };

    // URLクエリパラメータからjobIdまたはshiftIdを取得し、フィルターに反映
    const jobIdFromUrl = new URLSearchParams(location.search).get('jobId');
    const shiftIdFromUrl = new URLSearchParams(location.search).get('shiftId');
    if (jobIdFromUrl) {
      setSelectedJob(jobIdFromUrl);
    }
    if (shiftIdFromUrl) {
      setSelectedShift(shiftIdFromUrl);
    }

    // APIからデータを取得
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 店舗IDがない場合はエラーを表示
        if (!storeId) {
          setError('店舗IDが設定されていません。店舗情報を確認してください。');
          setIsLoading(false);
          return;
        }

        // 求人テンプレート取得
        const jobTemplatesData = await jobPostingApi.getAll({}, storeId);
        setJobTemplates(jobTemplatesData);

        // 職種情報取得
        try {
          const jobPositionsData = await jobPositionApi.getAll(storeId);
          setJobPositions(jobPositionsData);
        } catch (error) {
        }

        // 応募データ取得 - storeIdを必須パラメータとして渡す
        const applicationsData = await applicationApi.getAll({}, storeId);
        
        // バックエンドから取得したデータを処理
        const processedApplications = applicationsData.map(app => {
          // memo フィールドの値を確認（undefinedまたはnullの場合は空文字を設定）
          const memoValue = app.memo !== undefined && app.memo !== null ? String(app.memo) : '';
          
          return {
            ...app,
            memo: memoValue,    // memo フィールドを確実に文字列として保持
            notes: memoValue,   // notes フィールドにも同じ値を設定（表示用）
            user_rank: (app.user_rank as 'A' | 'B' | null) || null  // user_rankフィールドを追加
          };
        });
        
        setApplications(processedApplications);
        
        // 各応募のシフト情報を事前に取得
        const shiftPromises = processedApplications
          .filter(app => app.shift_id)
          .map(app => app.shift_id ? fetchShiftData(app.shift_id) : Promise.resolve(null));
        
        await Promise.all(shiftPromises);
        
        setError(null);

        // URLパラメータで指定された応募IDがある場合、該当する応募の詳細を自動表示
        const applicationId = getApplicationIdFromUrl();
        if (applicationId) {
          const targetApplication = processedApplications.find(app => 
            String(app.id) === String(applicationId)
          );
          
          if (targetApplication) {
            setSelectedApplication(targetApplication);
            setDetailDialogOpen(true);
            
            // URLからクエリパラメータを削除（履歴に残さない）
            navigate('/applicants', { replace: true });
          }
        }
        
      } catch (err: any) {
        if (err.message === '店舗IDが必要です') {
          setError('店舗IDが設定されていません。店舗情報を確認してください。');
        } else {
          setError('データの取得に失敗しました。');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [storeId, location.search, navigate, fetchShiftData]);
  
  const handleJobFilterChange = (event: SelectChangeEvent) => {
    setSelectedJob(event.target.value as string);
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  const handleViewDetails = async (application: ExtendedJobApplication) => {
    setSelectedApplication(application);

    // シフト情報を取得
    if (application.shift_id && storeId) {
      const shiftData = await fetchShiftData(application.shift_id);
      setSelectedApplicationShift(shiftData);
    } else {
      setSelectedApplicationShift(null);
    }

    // 同意済み労働条件通知書の有無を取得（未同意・PDF未保存の場合は404）
    setLaborAgreement(null);
    if (application.id && storeId) {
      try {
        const agreement = await laborConditionsApi.getAgreementByApplication(application.id, storeId);
        setLaborAgreement(agreement);
      } catch {
        // 404 = 同意済み通知書なし。ボタンを表示しないだけなので握りつぶす
      }
    }

    setDetailDialogOpen(true);
  };

  const handleDownloadLaborConditions = () => {
    if (!laborAgreement || !storeId) return;
    window.open(laborConditionsApi.getDownloadUrl(laborAgreement.agreement_id, storeId), '_blank');
  };
  
  const handleEditClick = (application: ExtendedJobApplication) => {
    setEditingApplication(application);
    
    // memo フィールドから直接値を取得（空文字の場合は空文字を設定）
    const memoValue = application.memo || '';
    setEditNotes(memoValue);
    
    // user_rank フィールドから値を取得（nullの場合は空文字を設定）
    const rankValue = application.user_rank || '';
    setEditRank(rankValue);

    // 課題114: 勤怠キャンセル理由をリセット
    setCancelAttendanceReason('');

    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingApplication || !editingApplication.id || storeId === null) return;

    setIsSaving(true);
    try {
      // 更新データを構築（採用/不採用のステータス変更は行わない。応募時点で採用確定するため）
      const updateData: { memo: string; user_rank: string | null } = {
        memo: editNotes,
        user_rank: editRank || null // ランクが空文字の場合はnullを設定
      };

      // 統合APIを使用して更新 - storeIdを必須パラメータとして渡す
      await applicationApi.update(editingApplication.id, updateData, storeId);

      // 応募リストを更新
      const updatedApplications = applications.map(app => {
        if (app.id === editingApplication.id) {
          // 更新されたデータで応募情報を更新
          const updated = {
            ...app,
            memo: editNotes,    // memo フィールドを更新
            notes: editNotes,   // notes フィールドも更新（表示用）
            user_rank: (editRank || null) as 'A' | 'B' | null
          };
          return updated;
        }
        return app;
      });

      // 状態を更新
      setApplications(updatedApplications);

      // 詳細ダイアログが開いている場合、選択中の応募も更新
      if (selectedApplication && selectedApplication.id === editingApplication.id) {
        setSelectedApplication({
          ...selectedApplication,
          memo: editNotes,
          notes: editNotes,
          user_rank: (editRank || null) as 'A' | 'B' | null
        });
      }

      setError(null);
      setEditDialogOpen(false);
    } catch (err: any) {
      if (err.message === '店舗IDが必要です') {
        setError('店舗IDが設定されていません。店舗情報を確認してください。');
      } else {
        // 再採用不可（勤怠がキャンセル/欠勤済み）等、バックエンドの理由をそのまま表示する
        setError(getApiErrorMessage(err, 'クルー情報の更新に失敗しました。'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 課題114: 採用確定済み応募の勤怠キャンセル（planned のみ。応募を cancelled に戻す）
  const handleCancelAttendance = async () => {
    if (!editingApplication || !editingApplication.id || storeId === null) return;

    setIsCancellingAttendance(true);
    try {
      await applicationApi.cancelAttendance(
        editingApplication.id,
        storeId,
        cancelAttendanceReason.trim() || undefined
      );

      // ローカル状態を cancelled に更新（handleSaveEdit と同じ楽観的更新方式）
      setApplications(prev => prev.map(app =>
        app.id === editingApplication.id
          ? { ...app, status: 'cancelled' as ExtendedJobApplication['status'], work_attendance_status: 'cancelled' }
          : app
      ));
      if (selectedApplication && selectedApplication.id === editingApplication.id) {
        setSelectedApplication({
          ...selectedApplication,
          status: 'cancelled' as ExtendedJobApplication['status'],
          work_attendance_status: 'cancelled',
        });
      }

      setCancelAttendanceReason('');
      setError(null);
      setEditDialogOpen(false);
    } catch (err: any) {
      // planned でない（既に出勤/退勤/承認済み）等、バックエンドの理由をそのまま表示する
      setError(getApiErrorMessage(err, '勤怠のキャンセルに失敗しました。'));
    } finally {
      setIsCancellingAttendance(false);
    }
  };

  // フィルタリングと並べ替え
  const filteredApplications = applications.filter(application => {
    const matchesJob = selectedJob === 'all' || String(application.job_id) === String(selectedJob);
    const matchesShift = selectedShift === 'all' || String(application.shift_id) === String(selectedShift);
    const matchesSearch = 
      application.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      application.applicant_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      application.applicant_phone.toLowerCase().includes(searchQuery.toLowerCase());
    
    // 勤務日フィルター
    let matchesDateFilter = true;
    if ((dateFromFilter || dateToFilter) && application.shift_id) {
      const shiftData = shiftDataCache[application.shift_id];
      if (shiftData && shiftData.shift_date) {
        const shiftDate = new Date(shiftData.shift_date);
        
        if (dateFromFilter) {
          matchesDateFilter = matchesDateFilter && shiftDate >= dateFromFilter;
        }
        
        if (dateToFilter) {
          // ToDateは当日の23:59:59まで含めるため、次の日の00:00:00と比較
          const toDateEnd = new Date(dateToFilter);
          toDateEnd.setDate(toDateEnd.getDate() + 1);
          matchesDateFilter = matchesDateFilter && shiftDate < toDateEnd;
        }
      } else if (dateFromFilter || dateToFilter) {
        // シフトデータがない場合は、フィルターが設定されていれば除外
        matchesDateFilter = false;
      }
    }
    
    return matchesJob && matchesShift && matchesSearch && matchesDateFilter;
  });

  // フィルタークリア機能
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedJob("all");
    setSelectedShift("all");
    setDateFromFilter(null);
    setDateToFilter(null);
  }; 

  // 求人名を取得する関数 
  const getJobTitle = (job_id: string): string => {
    // 求人テンプレートから job_title を探す
    const job = jobTemplates.find(job => 
      String(job.id) === String(job_id) ||
      (Number.isInteger(Number(job.id)) && Number.isInteger(Number(job_id)) && 
       Number(job.id) === Number(job_id))
    );
    
    if (job && job.job_title) {
      return job.job_title;
    }
    
    // 見つからなければデフォルト値を返す
    return '不明な求人';
  };

  // 職種名を取得する関数
  const getJobPositionName = (job_id: string): string => {
    // 求人テンプレートから position_id を取得
    const job = jobTemplates.find(job => 
      String(job.id) === String(job_id) ||
      (Number.isInteger(Number(job.id)) && Number.isInteger(Number(job_id)) && 
       Number(job.id) === Number(job_id))
    );
    
    if (job && job.position_id) {
      // position_id を使って職種マスタから職種名を取得
      const position = jobPositions.find(pos => 
        String(pos.id) === String(job.position_id) ||
        (Number.isInteger(Number(pos.id)) && Number.isInteger(Number(job.position_id)) && 
         Number(pos.id) === Number(job.position_id))
      );
      
      if (position && position.name) {
        return position.name;
      }
    }
    
    // 見つからなければ求人タイトルを返す
    return getJobTitle(job_id);
  };
  
  // 日時フォーマット関数
  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '不明';
    
    try {
      const date = new Date(dateString);
      
      // 無効な日付の場合
      if (isNaN(date.getTime())) {
        return '不明';
      }
      
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '不明';
    }
  };

  // シフト時刻フォーマット関数（時刻のみ）
  const formatShiftTime = (timeString?: string): string => {
    if (!timeString) return '不明';
    
    // 既に時刻形式の場合はそのまま返す
    if (timeString.includes(':')) {
      return timeString;
    }
    
    return '不明';
  };

  // シフト日付フォーマット関数
  const formatShiftDate = (dateString?: string): string => {
    if (!dateString) return '不明';
    
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        });
      }
      return '不明';
    } catch (error) {
      return '不明';
    }
  };

  // シフトの勤務時間を取得する関数
  const getShiftWorkTime = (shiftId?: string): string => {
    if (!shiftId) return '';
    
    const shiftData = shiftDataCache[shiftId];
    if (!shiftData) return '';
    
    const startTime = formatShiftTime(shiftData.start_time);
    const endTime = formatShiftTime(shiftData.end_time);
    
    if (startTime === '不明' || endTime === '不明') return '';
    
    return `${startTime} - ${endTime}`;
  };

  // シフトの勤務日を取得する関数
  const getShiftWorkDate = (shiftId?: string): string => {
    if (!shiftId) return '';
    
    const shiftData = shiftDataCache[shiftId];
    if (!shiftData) return '';
    
    return formatShiftDate(shiftData.shift_date);
  };
  
  // メモを安全に取得するヘルパー関数
  const getMemoContent = (application: ExtendedJobApplication): string => {
    return application.memo || '';
  };
  
  // 評価ランクを表示するためのヘルパー関数
  const getRankDisplay = (rank?: 'A' | 'B' | null): string => {
    if (!rank) return '-';
    return rank;
  };

  // 評価ランクのバッジコンポーネント
  const RankBadge: React.FC<{ rank?: 'A' | 'B' | null; size?: 'small' | 'medium' }> = ({ rank, size = 'small' }) => {
    if (!rank) return null;

    const configMap: Record<string, { bgcolor: string; color: string; border: string; label: string }> = {
      A: { bgcolor: '#e8f5e9', color: '#2e7d32', border: '#66bb6a', label: 'A' },
      B: { bgcolor: '#fff3e0', color: '#e65100', border: '#ffa726', label: 'B' },
    };
    const config = configMap[rank];
    if (!config) return null;

    const isSmall = size === 'small';

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: isSmall ? 22 : 26,
          height: isSmall ? 22 : 26,
          borderRadius: '6px',
          bgcolor: config.bgcolor,
          border: `1.5px solid ${config.border}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: isSmall ? 11 : 13,
            fontWeight: 700,
            color: config.color,
            lineHeight: 1,
          }}
        >
          {config.label}
        </Typography>
      </Box>
    );
  };
  
  // 採用ステータスのチップ表示（未設定は submitted 扱い）
  const StatusChip: React.FC<{ status?: string; size?: 'small' | 'medium' }> = ({ status, size = 'small' }) => {
    const config = APPLICATION_STATUS_CONFIG[status || 'submitted'] ?? { label: status || '-', color: 'default' as const };
    return <Chip label={config.label} color={config.color} size={size} sx={{ fontWeight: 600 }} />;
  };

  const toggleMobileExpand = (id: string) => {
    setExpandedMobileId(prev => (prev === id ? null : id));
  };

  const renderMobileCard = (application: ExtendedJobApplication) => {
    const appId = application.id ?? '';
    const isExpanded = expandedMobileId === appId;
    const workDate = getShiftWorkDate(application.shift_id);
    const workTime = getShiftWorkTime(application.shift_id);
    const memoContent = getMemoContent(application);

    return (
      <LeftAccentCard
        key={appId}
        accentColor="#0071e3"
        borderColor="#d9eafb"
        padding={0}
        sx={{ mb: 1.5 }}
      >
        <CardContent
          sx={{ pl: 2, pr: 1.5, py: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
          onClick={() => toggleMobileExpand(appId)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              fontSize="13px"
              fontWeight={600}
              color="#0071e3"
              flex={1}
              noWrap
            >
              {application.applicant_name}さん
            </Typography>
            <StatusChip status={application.status} />
            <RankBadge rank={application.user_rank} />
            <IconButton size="small" sx={{ p: 0.5, flexShrink: 0 }}>
              {isExpanded
                ? <ExpandMoreIcon sx={{ fontSize: 20, color: '#0071e3' }} />
                : <ChevronRightIcon sx={{ fontSize: 20, color: '#0071e3' }} />
              }
            </IconButton>
          </Stack>
        </CardContent>

        <Collapse in={isExpanded} unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight="medium">
                  {getJobPositionName(application.job_id)}
                </Typography>
              </Box>

              {workDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    勤務日: {workDate}
                  </Typography>
                </Box>
              )}

              {workTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    勤務時間: {workTime}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {application.applicant_email}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {application.applicant_phone}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  応募日: {formatDateTime(application.applied_date)}
                </Typography>
              </Box>

              {memoContent && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <NoteIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {memoContent}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => { e.stopPropagation(); handleViewDetails(application); }}
                sx={{
                  bgcolor: '#cce3f9',
                  color: '#0071e3',
                  fontWeight: 'bold',
                  borderRadius: '20px',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#b3d4f5', boxShadow: 'none' },
                }}
              >
                詳細
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => { e.stopPropagation(); handleEditClick(application); }}
                sx={{
                  bgcolor: '#cce3f9',
                  color: '#0071e3',
                  fontWeight: 'bold',
                  borderRadius: '20px',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#b3d4f5', boxShadow: 'none' },
                }}
              >
                編集
              </Button>
            </Box>
          </Box>
        </Collapse>
      </LeftAccentCard>
    );
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="応募者管理"
        icon={<PeopleIcon sx={{ color: '#898989', fontSize: 22 }} />}
        description={
          <Typography variant="body2" color="text.secondary">
            店舗に応募したクルーを確認できます
          </Typography>
        }
      />

      {storeId === null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          店舗IDが設定されていません。店舗情報を設定してください。
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            placeholder="クルーを検索..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="job-filter-label">求人</InputLabel>
            <Select
              labelId="job-filter-label"
              value={selectedJob}
              label="求人"
              onChange={handleJobFilterChange}
              disabled={storeId === null}
            >
              <MenuItem value="all">すべての求人</MenuItem>
              {jobTemplates.map((job) => (
                <MenuItem key={job.id} value={job.id.toString()}>
                  {job.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item spacing={2} sm={6} alignItems="center">
          <Grid container>
            <Grid item xs={4} sm={4}>
              <JapaneseDatePicker
                label="開始日"
                value={dateFromFilter}
                onChange={setDateFromFilter}
                disabled={storeId === null}
                placeholder="開始日を選択"
              />
            </Grid>
            <Grid
              item
              xs={1}
              sm={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "56px",
              }}
            >
              <Typography variant="body1">〜</Typography>
            </Grid>
            <Grid item xs={4} sm={4}>
              <JapaneseDatePicker
                label="終了日"
                value={dateToFilter}
                onChange={setDateToFilter}
                disabled={storeId === null}
                placeholder="終了日を選択"
              />
            </Grid>
            <Grid
              item
              xs={3}
              sm={2}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                height: "56px",
                marginLeft: "auto"
              }}
            >
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!searchQuery && selectedJob !== 'all' && !dateFromFilter && !dateToFilter}
                sx={{ height: "56px" }}
              >
                クリア
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            fullWidth
            startIcon={
              isDownloading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={downloadCSV}
            disabled={
              isDownloading ||
              filteredApplications.length === 0 ||
              storeId === null
            }
            sx={{
              height: "56px",
              bgcolor: green[600],
              "&:hover": { bgcolor: green[700] },
            }}
          >
            {isDownloading ? "ダウンロード中..." : "CSV出力"}
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ width: "100%", ...(isMobile && { boxShadow: 'none', border: 'none', bgcolor: 'transparent' }) }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : storeId === null ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              店舗IDが設定されていないため、データを取得できません
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ bgcolor: 'background.default' }}>
                {filteredApplications.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      応募データがありません
                    </Typography>
                  </Box>
                ) : (
                  filteredApplications.map((application) =>
                    renderMobileCard(application)
                  )
                )}
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>クルー名</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>職種</TableCell>
                      <TableCell>勤務日</TableCell>
                      <TableCell>勤務時間</TableCell>
                      <TableCell>メールアドレス</TableCell>
                      <TableCell>電話番号</TableCell>
                      <TableCell>応募日</TableCell>
                      <TableCell>メモ</TableCell>
                      <TableCell align="right">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredApplications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography
                            variant="body1"
                            sx={{ py: 2 }}
                            color="text.secondary"
                          >
                            応募データがありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredApplications.map((application) => {
                        const workDate = getShiftWorkDate(application.shift_id);
                        const workTime = getShiftWorkTime(application.shift_id);
                        const memoContent = getMemoContent(application);

                        return (
                          <TableRow key={application.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RankBadge rank={application.user_rank} size="medium" />
                                {application.applicant_name}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <StatusChip status={application.status} />
                            </TableCell>
                            <TableCell>
                              {getJobPositionName(application.job_id)}
                            </TableCell>
                            <TableCell>
                              {workDate ? (
                                <Typography variant="body2">
                                  {workDate}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {workTime ? (
                                <Typography variant="body2">
                                  {workTime}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{application.applicant_email}</TableCell>
                            <TableCell>{application.applicant_phone}</TableCell>
                            <TableCell>
                              {formatDateTime(application.applied_date)}
                            </TableCell>
                            <TableCell>
                              {memoContent ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {memoContent}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(application)}
                                color="info"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(application)}
                                color="primary"
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* 応募詳細ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          応募詳細
          <IconButton
            aria-label="close"
            onClick={() => setDetailDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedApplication && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      クルー情報
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        氏名
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedApplication.applicant_name}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        採用ステータス
                      </Typography>
                      <StatusChip status={selectedApplication.status} size="medium" />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        メールアドレス
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.applicant_email}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        電話番号
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.applicant_phone}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        応募日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(selectedApplication.applied_date)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        評価
                      </Typography>
                      <Typography variant="body1">
                        {getRankDisplay(selectedApplication.user_rank)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      応募先情報
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        求人
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {getJobTitle(selectedApplication.job_id)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        求人ID
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.job_id}
                      </Typography>
                    </Box>

                    {/* シフト情報の表示 */}
                    {selectedApplicationShift && (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            シフト日
                          </Typography>
                          <Typography variant="body1">
                            {formatShiftDate(
                              selectedApplicationShift.shift_date
                            )}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            開始時刻
                          </Typography>
                          <Typography variant="body1">
                            {formatShiftTime(
                              selectedApplicationShift.start_time
                            )}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            終了時刻
                          </Typography>
                          <Typography variant="body1">
                            {formatShiftTime(selectedApplicationShift.end_time)}
                          </Typography>
                        </Box>

                        {selectedApplicationShift.address && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              勤務地
                            </Typography>
                            <Typography variant="body1">
                              {selectedApplicationShift.address}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 同意済み労働条件通知書がある場合に表示 */}
              {laborAgreement && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        労働条件通知書
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          同意日時:{" "}
                          {laborAgreement.agreed_at
                            ? formatDateTime(laborAgreement.agreed_at)
                            : "---"}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadLaborConditions}
                        >
                          PDFをダウンロード
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* メモがある場合に表示 */}
              {(() => {
                const memoContent = getMemoContent(selectedApplication);

                return memoContent ? (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            メモ
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          paragraph
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {memoContent}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ) : null;
              })()}

              {selectedApplication.coverLetter && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        志望動機
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {selectedApplication.coverLetter}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* 履歴書がある場合の表示 */}
              {selectedApplication.resume && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        添付ファイル
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        // ここでは実際のファイルダウンロード機能はモックしています
                        onClick={() =>
                          alert("履歴書ダウンロード機能はモックです")
                        }
                      >
                        履歴書を表示
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          クルー情報を編集
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingApplication && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {editingApplication.applicant_name}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getJobTitle(editingApplication.job_id)}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  採用ステータス
                </Typography>
                <StatusChip status={editingApplication.status} size="medium" />
              </Box>

              {/* 課題114: 採用確定済み応募の勤怠キャンセル導線（planned のみ実行可能） */}
              {editingApplication.status === 'accepted' && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    勤怠のキャンセル
                  </Typography>
                  {editingApplication.work_attendance_status === 'planned' ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        この採用を取り消し、予定されている勤怠をキャンセルします。キャンセル後は同じ応募を再採用できません（必要な場合は新しいシフトへの応募として処理してください）。
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="キャンセル理由（任意）"
                        value={cancelAttendanceReason}
                        onChange={(e) => setCancelAttendanceReason(e.target.value)}
                        sx={{ mb: 1.5 }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={isCancellingAttendance}
                        onClick={handleCancelAttendance}
                        startIcon={isCancellingAttendance ? <CircularProgress size={16} color="inherit" /> : undefined}
                      >
                        {isCancellingAttendance ? 'キャンセル中...' : '勤怠をキャンセル'}
                      </Button>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {editingApplication.work_attendance_status === 'cancelled'
                        ? 'この勤怠は既にキャンセル済みです。'
                        : 'この勤怠は出勤・退勤・承認などが進んでいるため、この画面からはキャンセルできません。'}
                    </Typography>
                  )}
                </Box>
              )}

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="edit-rank-label">評価</InputLabel>
                <Select
                  labelId="edit-rank-label"
                  value={editRank}
                  label="評価"
                  onChange={(e) => setEditRank(e.target.value)}
                >
                  <MenuItem value="">なし</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="メモ"
                multiline
                rows={6}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                fullWidth
                placeholder="クルーに関するメモを入力してください"
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={
              isSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={isSaving || storeId === null}
            sx={{ bgcolor: green[600], "&:hover": { bgcolor: green[700] } }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Applicants;